<script setup>
import {Head, Link, useForm, usePage } from "@inertiajs/vue3";
import AuthenticationCard from "@/Components/AuthenticationCard.vue";
import AuthenticationCardLogo from "@/Components/AuthenticationCardLogo.vue";
import Checkbox from "@/Components/Checkbox.vue";
import InputError from "@/Components/InputError.vue";
import InputLabel from "@/Components/InputLabel.vue";
import TextInput from "@/Components/TextInput.vue";
import PasswordInput from "@/Components/PasswordInput.vue";
import GuestLayout from "../../Layouts/GuestLayout.vue";
import { Inertia } from "@inertiajs/inertia";
import { ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { getStappSolutionsUrl } from '@/helpers/stapp-links';
const { t: trans } = useI18n();

const page = usePage();
const selectedLocale = ref(page.props.locale)
const stappSolutionsUrl = getStappSolutionsUrl(page.props.locale);

const sessionMessage = page.props.flash.message || null

const props = defineProps({
    canResetPassword: Boolean,
    status: String,
    supportedLocales: Array,
    localeUserSelected: {
        type: Boolean,
        default: true,
    },
    redirectUri: {
        type: String,
        default: 'login',
    },
});

const form = useForm({
    email: "",
    password: "",
    remember: false,
});

const submit = () => {
    form.transform((data) => ({
        ...data,
        remember: form.remember ? "on" : "",
    })).post(route(props.redirectUri), {
        onFinish: () => form.reset("password"),
    });
};

function langToFlag(lang) {
  const parts = lang?.toLowerCase().split('-');
  return parts?.length > 1 ? parts[1] : parts[0];
}

const showLanguagePrompt = () => {
    Inertia.visit(window.location.pathname + '?forceLocalePrompt=1', {
        preserveState: false,
        preserveScroll: true,
    });
}
</script>

<template>
    <Head title="Log in" />

    <GuestLayout>
        <div class="flex min-h-screen grid-rows-3 bg-gray-100">
            <div class="flex flex-row h-40"></div>
            <div class="flex flex-col w-full lg:flex-row">
                <!-- Left Column - Login Form -->
                <div class="w-full lg:w-2/5 flex justify-center relative">
                    <!-- Taal wijzigen button -->
                    <button
                        v-if="localeUserSelected"
                        type="button"
                        @click="showLanguagePrompt"
                        class="absolute right-0 top-0 mt-4 mr-4 z-10 text-xs text-stapp-orange underline hover:text-stapp-orange-dark bg-white px-3 py-1 rounded shadow"
                    >
                    <img :src="`/build/flags/4x3/${langToFlag(selectedLocale)}.svg`" :alt="selectedLocale" width="20"
                  height="15" class="inline-block" style="border-radius: 2px;" />
                    </button>

                    <div class="w-full flex-col">
                        <div class="h-1/6 bg-stapp-bg"></div>
                            <AuthenticationCard>
                            <template #logo>
                                <AuthenticationCardLogo />
                            </template>

                            <div
                                v-if="status"
                                class="mb-4 font-medium text-sm text-green-600"
                            >
                                {{ status }}
                            </div>

                            <div v-if="sessionMessage" class="mb-4 font-medium text-sm text-red-600">
                                {{ sessionMessage }}
                            </div>

                            <div>
                                <p class="font-semibold mb-1 text-gray-700 mb-8">
                                    {{ trans("auth.Login") }}
                                </p>
                            </div>

                            <form @submit.prevent="submit">
                                <div>
                                    <InputLabel
                                        for="email"
                                        class="font-semibold"
                                        :value="trans('auth.Email')"
                                    />
                                    <TextInput
                                        id="email"
                                        v-model="form.email"
                                        type="email"
                                        class="mt-1 block w-full"
                                        required
                                        autofocus
                                        autocomplete="username"
                                    />
                                    <InputError
                                        class="mt-2"
                                        :message="form.errors.email"
                                    />
                                </div>

                                <div class="mt-8">
                                    <PasswordInput
                                        v-model="form.password"
                                        :errorMessage="form.errors.password"
                                        :label="trans('auth.Password')"
                                        :labelClass="'font-semibold'"
                                    />
                                </div>

                                <div class="block mt-4">
                                    <label class="flex items-center">
                                        <Checkbox
                                            v-model:checked="form.remember"
                                            name="remember"
                                        />
                                        <span class="ml-2 text-sm text-gray-600">{{
                                            trans("auth.Remember me")
                                        }}</span>
                                    </label>
                                </div>

                                <div class="flex items-center justify-end -mt-5">
                                    <Link
                                        :href="route('password.request')"
                                        class="text-sm text-stapp-orange rounded-md focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-stapp-orange"
                                    >
                                        {{ trans("auth.Forgot your password") }}?
                                    </Link>
                                </div>

                                <button
                                    :class="{ 'opacity-25': form.processing }"
                                    :disabled="form.processing"
                                    class="bg-primary text-white inline-flex items-center border border-transparent font-semibold hover:bg-primary focus:bg-primary active:bg-primary focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-2 transition ease-in-out duration-150 disabled:opacity-50 disabled:cursor-not-allowed mt-10 py-4 rounded-md px-8 text-sm"
                                >
                                    {{ trans("auth.Login") }}
                                </button>
                            </form>
                        </AuthenticationCard>
                    </div>
                </div>

                <!-- Right Column - Informational Text and Image/Video placeholder -->
                <div class="w-full lg:w-3/5 flex justify-center bg-white">
                    <div class="w-full flex-col">
                        <div class="h-1/6"></div>
                        <div class="w-full flex justify-center">
                            <div style="margin-top: 80px" class="max-w-lg justify-center">
                                <h2 class="text-lg font-semibold text-gray-700"> {{ trans("auth.Login in STAPP") }} </h2>
                                <p class="mt-4 text-sm text-gray-600"> {{ trans("auth.Om gebruik te kunnen maken van de oefensoftware van STAPP heb je een account nodig.") }} </p>
                                <ul class="mt-8 mb-8">
                                    <li class="flex items-start mt-2">
                                        <svg class="w-6 h-6 text-green-500 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                                            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M5 13l4 4L19 7"></path>
                                        </svg>
                                        <p class="w-4/5 text-sm text-gray-600"> {{ trans("auth.Therapeuten kunnen een abonnement krijgen via hun zorginstelling of praktijk") }}. </p>
                                    </li>
                                    <li class="flex items-start mt-6">
                                        <svg class="w-6 h-6 text-green-500 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                                            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M5 13l4 4L19 7"></path>
                                        </svg>
                                        <p class="w-4/5 text-sm text-gray-600"> {{ trans("auth.Personen met afasie kunnen via een zorginstelling worden uitnodigd en kunnen ook zelfstandig een abonnement afnemen.") }} </p>
                                    </li>
                                    <li class="flex items-start mt-6">
                                        <svg class="w-6 h-6 text-green-500 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                                            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M5 13l4 4L19 7"></path>
                                        </svg>
                                        <p class="w-4/5 text-sm text-gray-600">
                                            {{ trans("auth.Deze omgeving bevat vertrouwelijke en persoonlijke informatie. Zorg ervoor dat je inlogt vanaf een veilige locatie en bewust omgaat met jouw toegang.") }}
                                        </p>
                                    </li>
                                </ul>
                                <p class="mt-2 text-sm text-gray-600">
                                    {{ trans("auth.Kijk hiervoor op") }}
                                    <a :href="stappSolutionsUrl" target="_blank" rel="noopener noreferrer" class="text-stapp-orange"> {{ trans("auth.therapie platform voor afasie en spraakapraxie behandelingen") }}</a>
                                </p>
                            </div>
                        </div>

                        <div class="h-1/6"></div>
                    </div>
                </div>
            </div>
        </div>
    </GuestLayout>
</template>
